import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderMultiSectionDigitalClockTimeView } from "@mui/x-date-pickers/timeViewRenderers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import httpclient from "../../../Utils";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";

const AddBayPullingScheduleDialog = ({
  open,
  handleClose,
  refresh,
  bayList,
}) => {
  const [selectedBays, setSelectedBays] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inputValue, setInputValue] = useState("");

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const bayOptions = bayList.map((item) => item.bay);

  const handleBaySelect = (event, newValue) => {
    if (newValue && !selectedBays.includes(newValue)) {
      setSelectedBays([...selectedBays, newValue]);
      setInputValue("");
    }
  };

  const handleDeleteBay = (bayToDelete) => {
    setSelectedBays(selectedBays.filter((bay) => bay !== bayToDelete));
  };

  const handleSubmit = async () => {
    // Validation
    if (selectedBays.length === 0) {
      setError("Please select at least one bay");
      return;
    }

    if (!selectedTime) {
      setError("Please select a time");
      return;
    }

    setError("");
    setLoading(true);

    try {
      // Get time in 24-hour format (e.g., 2pm -> 14)
      const timeIn24HourFormat = dayjs(selectedTime).format("HH:mm");
      
      // Prepare payload with time in 24-hour format
      const payload = {
        bay: selectedBays,
        time: timeIn24HourFormat, // Sends as "14:30" for 2:30pm
      };

      console.log("Payload being sent:", payload); // For debugging

      const response = await httpclient.post("/bay-pulling-schedule/create", payload);

      if (response.data.status === 200) {
        showNotification("success", response.data.message);
        handleClose();
        // Reset form
        setSelectedBays([]);
        setSelectedTime(null);
        setInputValue("");
        // Refresh the table data
        refresh();
      } else {
        showNotification("info", response.data.message);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    handleClose();
    // Reset form after dialog closes
    setTimeout(() => {
      setSelectedBays([]);
      setSelectedTime(null);
      setError("");
      setInputValue("");
    }, 300);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="add-schedule-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="add-schedule-dialog-title">
        Add New Bay Pulling Schedule
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Time Selection - First */}
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Select Time"
                value={selectedTime}
                onChange={(newValue) => setSelectedTime(newValue)}
                viewRenderers={{
                  hours: renderMultiSectionDigitalClockTimeView,
                  minutes: renderMultiSectionDigitalClockTimeView,
                }}
                ampm={true}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    required: true,
                    // helperText: "Time will be sent in 24-hour format (e.g., 2:30pm → 14:30)",
                  },
                  layout: {
                    sx: {
                      '& .MuiDigitalClock-root': {
                        width: '100%',
                        maxHeight: '300px',
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Bay Selection - Second */}
          <Grid item xs={12}>
            <Autocomplete
              options={bayOptions}
              value={null}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              onChange={handleBaySelect}
              freeSolo
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Bays"
                  size="small"
                  placeholder="Type to search or add bay"
                  helperText="Type and press Enter to add multiple bays or click and select"
                />
              )}
            />
            
            {/* Selected Bays as Chips */}
            {selectedBays.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedBays.map((bay) => (
                  <Chip
                    key={bay}
                    label={bay}
                    onDelete={() => handleDeleteBay(bay)}
                    size="small"
                    sx={{
                      backgroundColor: "#7152F3",
                      color: "#fff",
                      "& .MuiChip-deleteIcon": {
                        color: "#fff",
                        "&:hover": {
                          color: "#ffcdd2",
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ mb: 2, mr: 2, mt: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          color="primary"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            backgroundColor: "#7152F3",
            "&:hover": { backgroundColor: "#5c34fa" },
            minWidth: "100px",
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBayPullingScheduleDialog;